import { ensure, Ok, sleep } from "effection";
import { openDB } from "idb";
import produce from "immer";
import { ActionWithPayload } from "robodux";
import {
  call,
  put,
  safe,
  select,
  spawn,
  take,
  takeEvery,
  takeLatest,
  takeLeading,
  updateStore,
} from "starfx";

import eventEmitter from "@app/contexts/emitter";
import i18n from "@app/i18n";
import { ensureArray } from "@app/service/helpers";
import { KeyMarker, ModalResult } from "@app/state/constants";
import { applog } from "@app/statefx/xThunks/app-log";
import { EnuAppOps } from "@app/types/insider";

import {
  createErr,
  identityResult,
  isErr,
  isJSONString,
  isOk,
  isResultLikeErr,
} from "../helpers";
import { isItemOk, sysSeparator, transformData } from "../helpers/dirTree";
import { runThunk, safeApi, safeLast, safeThunk } from "../helpers/fxhelpers";
import {
  deleteLocalDir,
  deleteServerFiles,
  openDelphiDirOfDownloadCode,
  openFXMLDetailsForm,
  openPdfBlobFromPath,
  readDirOfDownloadCode,
  spvPrepareSPVPdf,
  spvReadFullTree,
  spvReadInvoiceTree,
  updateDirOfDownloadCode,
} from "../routines/file-application-ops";
import { deleteFile, extractFileName } from "../routines/file-sys-ops";
import { schema } from "../schema";
import { TDialogs } from "../schema/notify.def";
import {
  invoiceSubir,
  selectFindPdfOfFXMID,
  selectUserFlags,
} from "../selectors/uiSpvMain.sel";
import { xThunk, xWorker } from "../xapis";
import { fetchDatabaseInWorker, wDel } from "./ds";
// import { sfxLoadSpvRelated } from './ds';
import {
  anafQuery,
  fxDbValidateCif,
  fxPutContractorMessageKeys,
  fxSaveContractor,
  getContractorsMainOne,
} from "./ds-contractors";
import { sfxFileSysGetObjects, sfxFileSysGetObjectsContent } from "./ds-fs";
import { sfxGetSpvMessages } from "./ds-spv";
import {
  nDeleteServerFiles,
  nProcessSpvMessages,
  xapiSpvReadFullTree,
} from "./n-file-sys-opts";
import { nmodReadDirOfDownloadCode } from "./nodeOps";
import { sfxNotify } from "./notify";
import { processSpvMessages } from "./prc-spvmsg";

import type { TFSDirTreeItem } from "../helpers/dirTree";
import type { Operation } from "starfx";
import type { DataGridTypes } from "devextreme-react/data-grid";

import type {
  TEmitent,
  TContractor,
  TSPVMessageKeys,
  TIvFiscal,
  TSPVUState,
  TSPVMsgReadState,
  TSPVMsgMarkedState,
  TExecution,
} from "@app/types/insider";

import type {
  TRepoSPVMessages,
  TRepoUiSpvGrid,
  ThunkCtx,
  TContractorFormSource,
} from "@app/types/insider";
import type { ApiCtx, Next } from "starfx";
type DataChange = DataGridTypes.DataChange;
const t = i18n.t.bind(i18n);
const db = schema;

function* parseableCtx(ctx: ApiCtx) {
  yield* sleep(1);
  return ctx.json;
}

function* dirTreePatch(msg: TRepoSPVMessages, items: TFSDirTreeItem[]) {
  const { QMY_CIF } = msg as TRepoSPVMessages;
  const cif = QMY_CIF.replace("RO", "").trim();
  const subDir = invoiceSubir(msg);
  yield* commonTreeSliceSliceUpdate(
    `${sysSeparator}${cif}${sysSeparator}${subDir}`,
    items
  );
}

function* commonTreeSliceSliceUpdate(
  payload: string,
  itemList: TFSDirTreeItem[]
) {
  const [, cif, invoice] = payload.split(sysSeparator);
  const fstree = yield* select(schema.fsDirTree.select);
  const newState = produce(fstree, (draft) => {
    if (!draft[cif]) {
      draft[cif] = {};
    }
    if (!draft[cif][invoice]) {
      draft[cif][invoice] = [];
    }
    draft[cif][invoice] = itemList;
    return draft;
  });
  yield* call(schema.update(schema.fsDirTree.set(newState)));
}
/* this is a very interesting use-case with a subtle twist
https://gist.github.com/VldMrgnn/31fe32782c758d8dc84d8ec7ac3bd2bd
*/
function* requestFileTree(msg: TRepoSPVMessages): Operation<void> {
  const { QMY_CIF } = msg as TRepoSPVMessages;
  const cif = QMY_CIF.replace("RO", "").trim();
  const subDir = invoiceSubir(msg);
  if (process.env.REACT_APP_MODE === "REACT") {
    // const justMonitoring =
    yield* safeApi(
      xapiSpvReadFullTree,
      `${sysSeparator}${cif}${sysSeparator}${subDir}`
    );
    //
  } else {
    const dirContent = yield* spvReadInvoiceTree({
      CIFNORO: cif,
      INVOICE: subDir,
    });
    const prsDirContent = yield* safeLast(() => identityResult(dirContent));
    if (isErr(prsDirContent)) {
      yield* runThunk(sfxNotify, {
        message: prsDirContent.error.message,
        type: "error",
        duration: 3000,
        position: "bottom",
      });
      return;
    }
    yield* dirTreePatch(msg, prsDirContent.value.items as TFSDirTreeItem[]);
  }
}
export const setCurrentSpvMessage = xThunk.create<TRepoSPVMessages>(
  "/xthnk/setCurrentSpvMessage",
  { supervisor: takeLatest },
  function* (ctx: ThunkCtx, next: Next) {
    const state = yield* select(db.uiSpvMain.select);
    const newState = {
      ...state,
      currentRecord: ctx.payload,
      otherCurrentRecord: {} as TRepoSPVMessages,
    };
    //this is well canceled on on takeLatest or on thunk exit;
    // yield* spawn(() => requestFileTree(ctx.payload)); // too expensive //
    yield* schema.update(db.uiSpvMain.set(newState));
    // this is a race between the user's click and the reading of the file system
    // from which we  bailout in:
    yield* sleep(3000);
    yield* next(); //<< nope ? //
  }
);

export const fxPreserveSpvMsgGridState = xThunk.create<TRepoUiSpvGrid>(
  "/xthnk/preserveSpvMsgGridState",
  { supervisor: takeLeading },
  function* (ctx: ThunkCtx, next: Next) {
    yield* schema.update(db.uiSpvGrid.set(ctx.payload));
    yield* next();
  }
);
// changed to UIInvoiceOverall...
export const uiSetDefaultIssuer = xThunk.create<TEmitent[]>(
  "/xthnk/uiSetDefaultIssuer",
  { supervisor: takeLeading },
  function* (ctx: ThunkCtx, next: Next) {
    const currentSelection = yield* select(db.uiInvoiceOverall.select);
    if (currentSelection.selectedCIF && currentSelection.selectedCIF !== "") {
      return;
    }
    //select the one with the lowest E_ID from Object.values(payload)
    const selectedIssuer = (Object.values(ctx.payload) as TEmitent[])
      .map((issuer) => ({ ...issuer, CIF: issuer.CIF.trim() }))
      .filter((issuer) => issuer.CIF)
      .sort((a, b) => a.E_ID - b.E_ID)[0];

    yield* schema.update(
      db.uiInvoiceOverall.update({
        key: "selectedCIF",
        value: selectedIssuer.CIF,
      })
    );
    yield* next();
  }
);

function* showMessageLater(): Operation<void> {
  yield* sleep(4_000);
  // if the parent does't finished in 4s,
  // show a waiting message
  // otherwis we never hit this
  yield* runThunk(sfxNotify, {
    message: "Se genereaza documentul...",
    type: "info",
    duration: 3000,
    position: "bottom",
  });
}
export const readPdfFile = xThunk.create<TRepoSPVMessages>(
  "/xthnk/readPdfFile",
  { supervisor: takeLeading },
  function* (ctx: ThunkCtx, next: Next) {
    yield* spawn(showMessageLater);
    const { FXM_ID } = ctx.payload;
    yield* runThunk(refreshLocalDir, ctx.payload);
    const pdfFile = yield* select((s) =>
      selectFindPdfOfFXMID(s, { FXMID: FXM_ID })
    );
    const pdfResult = yield* safeLast(
      spvPrepareSPVPdf({
        currentFile: pdfFile,
        currentRecord: JSON.stringify(ctx.payload),
      })
    );
    if (isErr(pdfResult)) {
      yield* runThunk(sfxNotify, {
        message: pdfResult.error.message,
        type: "error",
        duration: 5000,
        position: "top",
      });
      eventEmitter.emit("/xthnk/readPdfFile", "done");
      return;
    }
    if (isOk(pdfResult)) {
      // compute a suggested name;
      const { QRESULT_TYPE } = ctx.payload as TRepoSPVMessages;
      const prefix =
        QRESULT_TYPE === "FACTURA PRIMITA"
          ? "Factura_furnizor"
          : "Factura_client";
      const fileName0 = extractFileName(pdfResult.value);
      const fileName1 = fileName0.split("_UID")[0];
      const filePath = pdfResult.value as string;
      const suggestedFileName = `${prefix}_${fileName1}.pdf`;
      const rf = yield* safeLast(
        openPdfBlobFromPath(filePath, suggestedFileName)
      );
      //
      if (isErr(rf)) {
        // make a notification
        yield* runThunk(sfxNotify, {
          message: rf.error.message,
          type: "error",
          duration: 5000,
          position: "top",
        });
      }
      eventEmitter.emit("/xthnk/readPdfFile", "done");
    }
    yield* next();
  }
);

export const getContractorInfo = xThunk.create<TRepoSPVMessages>(
  "/xthnk/getContractorInfo",
  { supervisor: takeLeading },
  function* (ctx: ThunkCtx, next: Next) {
    //
    const { FXM_ID } = ctx.payload;
    if (!FXM_ID) {
      return;
    }
    ///,,,,,
    yield* next();
  }
);

// Helper function to extract substring between two strings
function stringBetween(source: string, start: string, end: string): string {
  const startIndex = source.indexOf(start) + start.length;
  const endIndex = source.indexOf(end, startIndex);
  return source.substring(startIndex, endIndex !== -1 ? endIndex : undefined);
}

export const fxFixUnknownContractors = xThunk.create(
  "/xthnk/fxFixUnknownContractors",
  { supervisor: takeLeading },
  function* (ctx: ThunkCtx, next: Next) {
    yield* safeThunk(processSpvMessages);
    yield* next();
  }
);

export const fxFixUnknownContractorsNJS = xThunk.create(
  "/xthnk/fxFixUnknownContractorsNJS",
  { supervisor: takeLeading },
  function* (ctx: ThunkCtx, next: Next) {
    // const oneYearAgo = new Date();
    // oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
    // const sqlOneYearAgo = dateToSqlDate(new Date(oneYearAgo));
    // const process = yield* safeLast(process_unknown(sqlOneYearAgo));

    yield* next();
  }
);

export const sfxShellOpen = xThunk.create<TRepoSPVMessages>(
  "/xthnk/sfxShellOpen",
  { supervisor: takeLeading },
  function* (ctx: ThunkCtx, next: Next) {
    if (!ctx.payload || !ctx.payload.FXM_ID) {
      return;
    }
    //
    const cresult = yield* safeLast(
      openDelphiDirOfDownloadCode(JSON.stringify(ctx.payload))
    );
    yield* next();
  }
);

type TkeyValue = {
  key: string;
  value: string;
};

export const fxUiUpdateSpvGrid = xThunk.create<TkeyValue>(
  "/xthnk/fxUiUpdateSpvGrid",
  { supervisor: takeEvery },
  function* (ctx: ThunkCtx, next: Next) {
    yield* schema.update([
      db.uiInvoiceOverall.update(ctx.payload),
      db.uiSpvGrid.update(ctx.payload),
    ]);
    yield* next();
  }
);
/* -------------------------------------------------------------------------- */

export const fxUiUpdateSpvMessage = xWorker.post<{ data: TSPVUState[] }>(
  "/xthnk/fxUiUpdateSpvMessage",
  { supervisor: takeEvery },
  function* (ctx: ApiCtx, next: Next) {
    ctx.request = ctx.req({
      url: "/select/post_ss_spv_user_status",
      method: "POST",
      body: JSON.stringify(ctx.payload),
    });
    yield* next();
  }
);
const amp = new Map<string, TSPVUState>();

export const theGate = xThunk.create(
  "thegate",
  { supervisor: takeLeading },
  function* (ctx: ThunkCtx, next: Next) {
    yield* sleep(1300);
    const workingAmp = new Map(amp);
    amp.clear();
    const who = yield* select(schema.whoami.select);
    for (const [key, value] of workingAmp) {
      console.log("execute key", key);
      // yield* call(fxUiUpdateSpvMessage.run(value));

      const {
        AMA_ID,
        FMUS_ID,
        FXM_ID,
        MARKED_STATE,
        READ_STATE,
        FXM_QDATA,
        FXM_QMYCIF,
        MESSAGE_NOTE,
      } = value;

      let tmpFmus = FMUS_ID;
      if (!FMUS_ID || FMUS_ID === 0 || FMUS_ID === -1) {
        yield* schema.update(schema.xId.decrement());
        const newId = yield* select(db.xId.select);
        tmpFmus = newId;
      }

      const existingDs = yield* select(db.SPVMessageUserState.selectById, {
        id: tmpFmus,
      });

      const newRecord = {
        ...existingDs,
        AMA_ID: AMA_ID ?? who.AmaId,
        FXM_ID,
        MARKED_STATE,
        READ_STATE,
        FMUS_ID: tmpFmus,
        FXM_QDATA,
        FXM_QMYCIF,
        MESSAGE_NOTE, //this is the personal note...
      };
      workingAmp.set(key, newRecord);
    }
    const accumRecords = Array.from(workingAmp.values());
    const allActions = accumRecords.map((value) =>
      db.SPVMessageUserState.patch({ [value.FMUS_ID]: value })
    );
    console.log("allActions", allActions);
    yield* schema.update(allActions);
    yield* call(fxUiUpdateSpvMessage.run({ data: accumRecords }));
    yield* next();
  }
);

export const pushChanges_ = xThunk.create<DataGridTypes.DataChange[]>(
  "/sfx/pushchanges",
  { supervisor: takeLeading },
  function* (ctx: ThunkCtx, next: Next) {
    const changes = ctx.payload;

    const who = yield* select(db.whoami.select);
    const workingAmp = new Map();
    for (const chg of changes) {
      const change = chg as unknown as DataChange;
      // we have only updates here
      if (change.type !== "update") {
        console.log("change", change);
        console.error("only updates are allowed");
        continue;
      }
      const oldRec = yield* select((s) =>
        selectUserFlags(s, { FXM_ID: change.key, AMA_ID: who.AmaId })
      );
      let tmpFmus = oldRec?.FMUS_ID;
      if (!tmpFmus || tmpFmus === 0 || tmpFmus === -1) {
        yield* schema.update(schema.xId.decrement());
        const newId = yield* select(db.xId.select);
        tmpFmus = newId;
      }

      const newRecord = {
        ...oldRec,
        ...change.data,
        FMUS_ID: tmpFmus,
        AMA_ID: who.AmaId,
        FXM_ID: change.key,
      };
      // yield* schema.update(db.SPVMessageUserState.add({[newRecord.FMUS_ID]:newRecord}));
      workingAmp.set(newRecord.FMUS_ID, newRecord);
    }
    const accumRecords = Array.from(workingAmp.values());
    const allActions = accumRecords.map((value) =>
      db.SPVMessageUserState.add({ [value.FMUS_ID]: value })
    );
    yield* schema.update(allActions);
    yield* call(fxUiUpdateSpvMessage.run({ data: accumRecords }));

    yield* next();
  }
);

export const pushChanges = xThunk.create<DataGridTypes.DataChange[]>(
  "/sfx/pushchanges",
  { supervisor: takeLeading },
  function* (ctx: ThunkCtx, next: Next) {
    yield* call(
      applog.run({ operation: EnuAppOps.spvlist_mark_start, value: null })
    );

    const changes = ctx.payload;

    const who = yield* select(db.whoami.select);
    const workingAmp = new Map();
    for (const chg of changes) {
      const change = chg as unknown as DataChange;
      // we have only updates here
      if (change.type !== "update") {
        console.log("change", change);
        console.error("only updates are allowed");
        continue;
      }
      const oldRec = yield* select((s) =>
        selectUserFlags(s, { FXM_ID: change.key, AMA_ID: who.AmaId })
      );
      let tmpFmus = oldRec?.FMUS_ID;
      if (!tmpFmus || tmpFmus === 0 || tmpFmus === -1) {
        yield* schema.update(schema.xId.decrement());
        const newId = yield* select(db.xId.select);
        tmpFmus = newId;
      }

      const newRecord = {
        ...oldRec,
        ...change.data,
        FMUS_ID: tmpFmus,
        AMA_ID: who.AmaId,
        FXM_ID: change.key,
      };
      // yield* schema.update(db.SPVMessageUserState.add({[newRecord.FMUS_ID]:newRecord}));
      workingAmp.set(newRecord.FMUS_ID, newRecord);
    }
    const accumRecords = Array.from(workingAmp.values());
    const allActions = accumRecords.map((value) =>
      db.SPVMessageUserState.add({ [value.FMUS_ID]: value })
    );
    yield* schema.update(allActions);
    yield* call(fxUiUpdateSpvMessage.run({ data: accumRecords }));

    yield* call(
      applog.run({ operation: EnuAppOps.spvlist_mark_end, value: null })
    );

    yield* next();
  }
);
export const pushActions = xThunk.create<Partial<TSPVUState>>(
  "/sfx/pushaction",
  { supervisor: takeEvery },
  function* (ctx: ThunkCtx, next: Next) {
    const myObj = { ...ctx.payload };
    db.SPVMessageUserState.patch({ [myObj.FMUS_ID]: myObj });
    amp.set(myObj.FMUS_ID, myObj);
    yield* next();
  }
);

/* -------------------------------------------------------------------------- */

export const spvMessageFileDownloadUnpack = xThunk.create<TRepoSPVMessages>(
  "/xthnk/spvMessageFileDownloadUnpack2",
  { supervisor: takeLeading },
  function* (ctx: ThunkCtx, next: Next) {
    const fResult = yield* updateDirOfDownloadCode(JSON.stringify(ctx.payload));
    console.log("fResult", fResult);
    if (isErr(fResult)) {
      yield* runThunk(sfxNotify, {
        message: fResult.error.message,
        type: "error",
        duration: 3000,
        position: "bottom",
      });
      yield* next(); // the question //
      return;
    }

    const { QMY_CIF } = ctx.payload as TRepoSPVMessages;
    const cif = QMY_CIF.replace("RO", "").trim();
    const subDir = yield* call(() => invoiceSubir(ctx.payload));

    if (process.env.REACT_APP_MODE === "REACT") {
      yield* safeApi(
        xapiSpvReadFullTree,
        `${sysSeparator}${cif}${sysSeparator}${subDir}`
      );
    } else {
      const dirContent = yield* spvReadInvoiceTree({
        CIFNORO: cif,
        INVOICE: subDir,
      });
      const prsDirContent = yield* safeLast(() => identityResult(dirContent));
      if (isErr(prsDirContent)) {
        yield* runThunk(sfxNotify, {
          message: prsDirContent.error.message,
          type: "error",
          duration: 3000,
          position: "bottom",
        });
        yield* next();
        return;
      }
      yield* call(
        dirTreePatch(ctx.payload, prsDirContent.value.items as TFSDirTreeItem[])
      );
    }
    yield* call(
      fetchDatabaseInWorker.run({
        postBack: true,
        pbkType: "PBK_IFCHANGED",
        workerEndpoint: "fetchspvrelated",
      })
    );
    yield* next();
  }
);

export const refreshLocalDir = xThunk.create<TRepoSPVMessages>(
  `/xthnk/refreshLocalDir`,
  { supervisor: takeLeading },
  function* (ctx: ThunkCtx, next: Next) {
    const { FXM_ID } = ctx.payload;
    const fResult =
      process.env.REACT_APP_MODE === "DELPHI"
        ? yield* readDirOfDownloadCode(JSON.stringify(ctx.payload))
        : yield* safe(
            nmodReadDirOfDownloadCode.run(JSON.stringify(ctx.payload))
          );

    if (isErr(fResult)) {
      ctx.result = fResult;
    } else {
      const theKey = `FXM_ID::${FXM_ID}`;
      // const parsed = JSON.parse(fResult.value as string);
      if (theKey) {
        if (fResult.value?.items && Array.isArray(fResult.value.items)) {
          yield* call(
            schema.update(db.fileSysLocalDisk.add({ [theKey]: fResult.value }))
          );
        } else {
          ///if it comes empty...
          yield* call(
            schema.update(db.fileSysLocalDisk.add({ [theKey]: undefined }))
          );
        }
      }
      ctx.result = fResult;
    }
    yield* next();
  }
);

export const spvDirFullTreeSync = xThunk.create(
  "/xthnk/spvDirFullTreeSync",
  { supervisor: takeLeading },
  function* (ctx: ThunkCtx, next: Next) {
    if (process.env.REACT_APP_MODE === "REACT") {
      yield* safeApi(xapiSpvReadFullTree, ".");
    } else {
      const dirtree = yield* spvReadFullTree(JSON.stringify(ctx.payload));
      const result = yield* safeLast(() => identityResult(dirtree));
      if (isErr(result)) {
        return;
      }
      if (isItemOk(result)) {
        const itemList = result.value.items as TFSDirTreeItem[];
        const data = transformData(itemList);
        yield* call(schema.update(schema.fsDirTree.set(data)));
      }
    }
    yield* next();
  }
);

type TFxmFileName = {
  FXMID: string;
  fileName: string;
};
export const spvDeleteLocalFile = xThunk.create<TFxmFileName>(
  "/xthnk/spvLocalDirDelete",
  { supervisor: takeLeading },
  function* (ctx: ThunkCtx, next: Next) {
    const { FXMID, fileName } = ctx.payload;
    const currentMsg = yield* select(schema.uiSpvMain.select);

    if (!currentMsg.currentRecord) {
      // inconsistent state, should not happen
      return;
    }
    if (currentMsg.currentRecord.FXM_ID !== FXMID) {
      // inconsistent state, should not happen
      return;
    }
    //
    const hardVDir = yield* select(schema.fileSysLocalDisk.selectTable);
    const _ourDir = hardVDir?.[`FXM_ID::${FXMID}`];
    const ourDir = { ..._ourDir };
    //
    const ourFileOj = ensureArray(ourDir?.items)?.find(
      (f) => f.name === fileName
    );
    const ourFile = ourFileOj.fullPath;

    if (!ourFile) {
      return;
    }

    yield* safeLast(deleteFile(ourFile));

    yield* runThunk(refreshLocalDir, currentMsg.currentRecord);

    yield* next();
  }
);

export const spvDeleteLocalDir = xThunk.create<TRepoSPVMessages>(
  "/xthnk/spvDeleteLocalDir",
  { supervisor: takeLeading },
  function* (ctx: ThunkCtx, next: Next) {
    const { FXM_ID } = ctx.payload;
    const opResult = yield* safeLast(
      deleteLocalDir(JSON.stringify(ctx.payload))
    );
    if (isErr(opResult)) {
      yield* runThunk(sfxNotify, {
        message: opResult.error.message,
        type: "error",
        duration: 3000,
        position: "bottom",
      });
      return;
    }
    yield* runThunk(sfxNotify, {
      message: "Directorul a fost sters",
      type: "success",
      duration: 1000,
      position: "bottom",
    });
    yield* schema.update(
      schema.fileSysLocalDisk.remove([`FXM_ID::${FXM_ID}}`])
    );
    yield* dirTreePatch(ctx.payload, undefined as TFSDirTreeItem[]);
    yield* call(sfxGetSpvMessages.run());
    yield* runThunk(refreshLocalDir, ctx.payload);
    yield* next();
  }
);
// WORKER POC

function* waitForCtx(ctx: ApiCtx, next: Next) {
  yield* spawn(function* () {
    yield* sleep(20_000);
    yield* put({ type: `/cancel${ctx.key}` });
  });
  yield* next();
  while (true) {
    const someResult = yield* take([`/done${ctx.key}`, `/cancel${ctx.key}`]);
    if (someResult.type === `/cancel${ctx.key}`) {
      ctx.result = createErr(new Error("Operation timed out"));
    } else {
      ctx.json = someResult.payload as any;
    }
    break;
  }
}

export const spvDeleteServerDir = xWorker.delete<TRepoSPVMessages>(
  "/wdel/spvDeleteServerDir",
  { supervisor: takeLeading },
  [
    function* (ctx: ApiCtx, next: Next) {
      const { FXM_ID } = ctx.payload;

      const dbSizeBeforeUpdate = yield* select(
        schema.SPVMessages.selectTableAsList
      );
      const sizeBefore = dbSizeBeforeUpdate.length;
      //

      ctx.request = ctx.req({
        method: "DELETE",
        body: JSON.stringify({ data: ctx.payload }),
        headers: {
          "Content-Type": "application/json",
        },
      });
      yield* next();
      console.log("ctx", ctx);
      const opResult = yield* safeLast(identityACtx(ctx));
      console.log("opResult", opResult);
      if (isErr(opResult)) {
        yield* runThunk(sfxNotify, {
          message: opResult.error.message,
          type: "error",
          duration: 3000,
          position: "bottom",
        });
        return;
      }
      yield* schema.update(schema.SPVMessages.remove([FXM_ID]));

      yield* runThunk(sfxNotify, {
        message: "Directorul a fost sters",
        type: "success",
        duration: 1000,
        position: "bottom",
      });

      yield* call(
        schema.update(schema.fileSysLocalDisk.remove([`FXM_ID::${FXM_ID}}`]))
      );
      yield* dirTreePatch(ctx.payload, undefined as TFSDirTreeItem[]);

      yield* call(sfxGetSpvMessages.run());
      yield* runThunk(refreshLocalDir, ctx.payload);
      const dbSizeAfterUpdate = yield* select(
        schema.SPVMessages.selectTableAsList
      );
      const sizeAfter = dbSizeAfterUpdate.length;

      const diff = sizeBefore - sizeAfter;

      if (diff > 0) {
        yield* runThunk(sfxNotify, {
          message:
            diff === 1
              ? `Mesajul a fost sters.`
              : `Au fost sterse ${diff} mesaje`,
          type: "success",
          duration: 3000,
          position: "bottom",
        });
      }
    },
    waitForCtx,
  ]
);

// THE CLASSIC WAY
export const spvDeleteServerDir_ = xThunk.create<TRepoSPVMessages>(
  "/xthnk/spvDeleteServerDir",
  { supervisor: takeLeading },
  function* (ctx: ThunkCtx, next: Next) {
    const { FXM_ID } = ctx.payload;

    const dbSizeBeforeUpdate = yield* select(
      schema.SPVMessages.selectTableAsList
    );
    const sizeBefore = dbSizeBeforeUpdate.length;
    //
    const opResult =
      process.env.REACT_APP_MODE === "DELPHI"
        ? yield* safeLast(deleteServerFiles(JSON.stringify(ctx.payload)))
        : yield* safeLast(nDeleteServerFiles.run(ctx.payload));

    console.log("opResult", opResult);
    if (isErr(opResult)) {
      yield* runThunk(sfxNotify, {
        message: opResult.error.message,
        type: "error",
        duration: 3000,
        position: "bottom",
      });
      return;
    }
    yield* schema.update(schema.SPVMessages.remove([FXM_ID]));

    yield* call(
      wDel.run({
        table: "SPVMessages",
        index: "_primary_",
        lower: FXM_ID,
        upper: FXM_ID,
      })
    );

    yield* runThunk(sfxNotify, {
      message: "Directorul a fost sters",
      type: "success",
      duration: 1000,
      position: "bottom",
    });

    yield* call(
      //todo: implement fileSysLocalDisk in worker. fetch the data at least
      schema.update(schema.fileSysLocalDisk.remove([`FXM_ID::${FXM_ID}}`]))
    );
    yield* dirTreePatch(ctx.payload, undefined as TFSDirTreeItem[]);
    yield* call(sfxGetSpvMessages.run());
    yield* runThunk(refreshLocalDir, ctx.payload);

    const dbSizeAfterUpdate = yield* select(
      schema.SPVMessages.selectTableAsList
    );
    const sizeAfter = dbSizeAfterUpdate.length;

    const diff = sizeBefore - sizeAfter;

    if (diff > 0) {
      yield* runThunk(sfxNotify, {
        message:
          diff === 1
            ? `Mesajul a fost sters.`
            : `Au fost sterse ${diff} mesaje`,
        type: "success",
        duration: 3000,
        position: "bottom",
      });
    }

    yield* next();
  }
);

export const svpReceivingSpvMessage = xThunk.create<string>(
  "/xthnk/svpReceivingSpvMessage",
  { supervisor: takeEvery },
  function* (ctx: ThunkCtx, next: Next) {
    //
    // vezi ce forma are
    // desfa-l si scrie-l in store
    if (isResultLikeErr(ctx.payload)) {
      const [message, key] = ctx.payload.error.split(KeyMarker);
      yield* updateStore(schema.notify.set({ message: message }));
      yield* next();
      return;
    }

    if (isOk(ctx.payload)) {
      if (isJSONString(ctx.payload.value)) {
        //[##findme:1233##indelphiandjavascript]
        const parsed = JSON.parse(ctx.payload.value as string);
        if (!parsed.__messageID) {
          return;
        }
        const initialMsg = yield* select((s) =>
          schema.SPVMessages.selectById(s, { id: parsed.__messageID })
        );
        if (!initialMsg) {
          return;
        }
        const newMsg = {
          ...initialMsg,
          FSH_ID: parsed?.FSH_ID || null,
          REFERINTA_INTERNA: parsed?.InvoiceId_BT1 || "",
          DENUMIRE_PARTENER: parsed.ClientName,
          IS_TEMPORARY: true,
          syncResult: "OK",
        } as TRepoSPVMessages;

        yield* schema.update(
          schema.SPVMessages.patch({ [newMsg.FXM_ID]: { ...newMsg } })
        );
        yield* schema.update(
          schema.notify.set({
            message: `S-a prelucrat factura ${parsed?.InvoiceId_BT1} de la ${parsed.ClientName}`,
          })
        );
      }
    }
    yield* next();
  }
);

//resets the form
export const incrementCff = xThunk.create(
  "/xthnk/incrementCff",
  { supervisor: takeLeading },
  function* (ctx: ThunkCtx, next: Next) {
    yield* schema.update(db.contractors_cff_num.increment(1));
    yield* next();
  }
);
// resets the form
export const incrementCffTmp = xThunk.create(
  "/xthnk/incrementCffTmp",
  { supervisor: takeLeading },
  function* (ctx: ThunkCtx, next: Next) {
    yield* schema.update(db.contractors_temp_num.increment(1));
    yield* next();
  }
);
// resets the form
export const incrementCffAnaf = xThunk.create(
  "/xthnk/incrementCffAnaf",
  { supervisor: takeLeading },
  function* (ctx: ThunkCtx, next: Next) {
    yield* schema.update(db.contractors_anaf_num.increment(1));
    yield* next();
  }
);
// resets the form
export const incrementCffArchive = xThunk.create(
  "/xthnk/incrementCffArchive",
  { supervisor: takeLeading },
  function* (ctx: ThunkCtx, next: Next) {
    yield* schema.update(db.contcatlist_num.increment(1));
    yield* next();
  }
);

function* identityACtx(ctx: ApiCtx) {
  yield* sleep(1);
  return ctx;
}

// first do some more checks
/* 
Possible results:
0 --OK, No conflict;
-1; -- EMPTY-ISH CIF
-2; -- Attempting to update an existing different CIF
-3; -- Attempting to create a new entry with  an existing CIF
-4; -- Attempting to update a non-existent CIF. Unable to fix;
-5; -- Attempting to update a non-existent record;
-6; -- Probabbly fixed. Re-run the whole procedure;
-7; -- MULTIPLE RECORDS WITH THE SAME CIFNORO;
*/
// THIS IS FOR DEBUG AND TESTING PURPOSES
// fxDbValidateCif.use(function* (ctx, next) {
//   ctx.response = new Response(
//     JSON.stringify([{ ERROR_CODE: -7, CONFLICTING_CFFID: 20 }]),
//   );
//   yield* next();
// });

function* showDialogOf(
  CustomName: TDialogs["name"],
  dbValidationResult?: { ERROR_CODE: number; CONFLICTING_CFFID: number }
): Operation<TDialogs["result"]> {
  yield* schema.update(
    schema.dialogs.set({
      name: CustomName,
      result: null,
      params: dbValidationResult,
    })
  );
  while (true) {
    const waitFor = yield* take("/sfx/setDialogResult");
    const { payload } = waitFor as ActionWithPayload<Partial<TDialogs>>;
    const dResult = payload.result;
    yield* schema.update(schema.dialogs.reset());
    return dResult;
  }
}

function* showAbort() {
  yield* runThunk(sfxNotify, {
    message: "Operatiune anulata",
    type: "info",
    duration: 2000,
    position: "bottom",
  });
  sleep(2000);
  return;
}

let called = 0;
export const saveContractorToDb = xThunk.create<TContractor>(
  "/xthnk/saveContractorToDb",
  { supervisor: takeEvery },
  function* (ctx: ThunkCtx, next: Next) {
    const prep = ctx.payload;
    let prep00 = {
      ...prep,
      Address: `${prep.Address} || ${prep?.__address__continuation || ""}`,
    };

    const dbValidation = yield* call(fxDbValidateCif.run(prep00));
    //we should interpret the result
    const parsedDbValidation = yield* safeLast(() =>
      identityACtx(dbValidation)
    );
    if (isErr(parsedDbValidation)) {
      yield* runThunk(sfxNotify, {
        message: parsedDbValidation.error.message,
        type: "error",
        duration: 3000,
        position: "bottom",
      });
      yield* next();
      return;
    }

    const [{ ERROR_CODE, CONFLICTING_CFFID }] = parsedDbValidation.value || [
      { ERROR_CODE: null, CONFLICTING_CFFID: null },
    ];

    switch (ERROR_CODE) {
      case 0: {
        // everything is ok
        break;
      }
      case -1: {
        //show dialog of ('DBVALIADATION-OF-1')
        const dialogResult = yield* call(showDialogOf("DBVALIADATION-OF-1"));
        if (dialogResult === ModalResult.mrOk) {
          //that means we can continue by setting the TContrator.UsesRoEfactura to false;
          prep00 = produce(prep00, (draft) => {
            draft.UsingRoEfactura = 0;
            return draft;
          });
        } else {
          yield* call(showAbort);
          yield* next();
          return;
        }
        break;
      }
      case -2: {
        // -2; -- Attempting to update an existing different CIF
        yield* call(
          showDialogOf("DBVALIADATION-OF-2", {
            ERROR_CODE,
            CONFLICTING_CFFID,
          })
        );
        yield* call(showAbort);
        yield* next();
        return;
      }
      case -3: {
        //-3; -- Attempting to create a new entry with  an existing CIF
        yield* call(
          showDialogOf("DBVALIADATION-OF-3", {
            ERROR_CODE,
            CONFLICTING_CFFID,
          })
        );
        yield* call(showAbort);
        yield* next();
        return;
      }
      case -4: {
        // -4; -- Attempting to update a non-existent CIF. Unable to fix;
        yield* call(
          showDialogOf("DBVALIADATION-OF-4", {
            ERROR_CODE,
            CONFLICTING_CFFID,
          })
        );
        yield* call(showAbort);
        yield* next();
        return;
      }
      case -5: {
        // -5; -- Attempting to update a non-existent record;
        yield* call(
          showDialogOf("DBVALIADATION-OF-5", {
            ERROR_CODE,
            CONFLICTING_CFFID,
          })
        );
        yield* call(showAbort);
        yield* next();
        return;
      }
      case -6: {
        // -6; -- Probabbly fixed. Re-run the whole procedure;
        if (++called % 2 === 1) {
          const fn = ctx.actionFn as any;
          yield* put(fn(ctx.payload));

          yield* next();
          return;
        } else {
          yield* call(
            showDialogOf("DBVALIADATION-OF-6", {
              ERROR_CODE,
              CONFLICTING_CFFID,
            })
          );
          yield* call(showAbort);
          yield* next();
          return;
        }
      }
      case -7: {
        //-7; -- MULTIPLE RECORDS WITH THE SAME CIFNORO;
        yield* call(
          showDialogOf("DBVALIADATION-OF-7", {
            ERROR_CODE,
            CONFLICTING_CFFID,
          })
        );
        yield* call(showAbort);
        yield* next();
        return;
      }
      default: {
        yield* runThunk(sfxNotify, {
          message: "Erori necunoscute.",
          type: "error",
          duration: 3000,
          position: "bottom",
        });
        yield* next();
        return;
      }
    }
    const newIDCtx = yield* call(fxSaveContractor.run(prep00));
    const newResponse = yield* safeLast(parseableCtx(newIDCtx));

    if (isErr(newResponse)) {
      yield* runThunk(sfxNotify, {
        message: newResponse.error.message,
        type: "error",
        duration: 3000,
        position: "bottom",
      });
      yield* next();
      return;
    }
    const [{ NEWID: newID }] = newResponse.value || [{ NEWID: undefined }];
    //
    if (!newID) {
      yield* runThunk(sfxNotify, {
        message: "Eroare la salvare. Id-ul nu a fost primit.",
        type: "error",
        duration: 3000,
        position: "bottom",
      });
      yield* next();
      return;
    }
    const intialId = ctx.payload.CffId;
    //
    if ((intialId || -1) < 1) {
      yield* call(
        schema.update(
          schema.notify.set({
            message: "Datele partenerului comercial au fost adaugate",
          })
        )
      );
      //we need to insert the new id in the message
      yield* call(getContractorsMainOne.run(newID));

      // update the id of the current selected entity on form:
      // setContractorsEditingProp({ mainCffId: -1 })
      yield* schema.update(
        schema.uiContractorsEditing.update({ key: "mainCffId", value: newID })
      );

      //if we run this from the contractor form, we don't have a message id
      if (ctx.payload.__messageID) {
        const mkeys = yield* select(schema.SPVMessageKeys.selectTable);
        const existingMkey = mkeys[ctx.payload.__messageID];
        /* 
          CFF_ID(pin):84
          CFFTMP_ID(pin):null
          CLI_FF_ID(pin):-1
          FZ_F_ID(pin):null
        */
        const newMkey = { ...existingMkey, CFF_ID: newID } as TSPVMessageKeys;

        const keyResult = yield* call(
          fxPutContractorMessageKeys.run(newMkey as any)
        ); //??
        const parsedResult = yield* safeLast(parseableCtx(keyResult));
        if (isErr(parsedResult)) {
          yield* runThunk(sfxNotify, {
            message: parsedResult.error.message,
            type: "error",
            duration: 3000,
            position: "bottom",
          });
          yield* next();
          return;
        }
        yield* call(
          schema.update(
            schema.SPVMessageKeys.patch({ [ctx.payload.__messageID]: newMkey })
          )
        );
      }
      //advertise
      yield* runThunk(sfxNotify, {
        message: "Datele partenerului comercial au fost adaugate.",
        type: "success",
        duration: 3000,
        position: "bottom",
      });
      yield* next();
      return;
    }
    if (intialId !== newID) {
      // this is impossible at insert/update.
      // either insert as above, either update which means the id is the same.
      // this is a bug.
      yield* runThunk(sfxNotify, {
        message: "Eroare la salvare. Id-ul nu corespunde.",
        type: "error",
        duration: 3000,
        position: "bottom",
      });

      yield* next();
      return;
    }
    // update the contractor:

    // update the message: we don't
    // update the message keys in the database and here /
    yield* call(getContractorsMainOne.run(newID));
    //advertise
    yield* runThunk(sfxNotify, {
      message: "Datele partenerului comercial au fost actualizate.",
      type: "success",
      duration: 3000,
      position: "bottom",
    });

    // we are using a shared procedure tha inserst or updates the CLIENTI_CFF
    // next we have to ammend the dependencies.
    // todo this at the end, alltogether with anaf and contact import
    // on success is a lot of work to do:
    // updaer message, update contractor, update message_keys
    // advertise
    // this also might have impact on the temp dir in appdata or at least for the filenames.

    yield* next();
  }
);
function* runLater(): Operation<void> {
  yield* sleep(2_000);
  eventEmitter.emit("/xthnk/readPdfFile", "start");
}
function isCuiValid(cui: string): boolean {
  // is made only from digits and has less than 10 digits
  return /^\d{1,9}$/.test(cui);
}

export const fxExecuteAnafQuery = xThunk.create<string>(
  "/xthnk/fxExecuteAnafQuery",
  { supervisor: takeLeading },
  function* (ctx: ThunkCtx, next: Next) {
    let cui = ctx.payload;
    if (ctx.payload.includes("--force")) {
      cui = ctx.payload.replace("--force", "").trim();
    } else {
      const anafDs = yield* select((s) =>
        db.contractors_anaf.selectById(s, { id: cui })
      );
      if (
        anafDs &&
        typeof anafDs === "object" &&
        Object.values(anafDs).length > 0
      ) {
        yield* next();
        return;
      }
    }
    yield* spawn(runLater);
    if (process.env.REACT_APP_MODE === "REACT") {
      yield* call(anafQuery.run({ cui: cui }));
    } else {
      //DELPHI//
      const norocui = cui.replaceAll("RO", "").replaceAll(" ", "");
      // validating the  cui (in delphi we skip this)
      if (isCuiValid(norocui)) {
        //TODO: we could map through the neighbours and get the data for all of them  in one go
        const today = new Date().toISOString().slice(0, 10);
        const wellFormed = JSON.stringify([{ cui: cui, data: today }]);
        yield* call(anafQuery.run({ cui: wellFormed }));
      } else {
        yield* runThunk(sfxNotify, {
          message: "CUI-ul nu este valid.",
          type: "error",
          duration: 3000,
          position: "bottom",
        });
        yield* next();
        return;
      }
    }
    yield* next();
    eventEmitter.emit("/xthnk/readPdfFile", "done");
  }
);

export const setCurrentMirrorContactSelection = xThunk.create<number>(
  "/xthnk/setCurrentMirrorContactSelection",
  { supervisor: takeLeading },
  function* (ctx: ThunkCtx, next: Next) {
    yield* sleep(1);
    const spvMain = yield* select(schema.uiSpvMain.select);

    yield* schema.update([
      db.uiSpvGrid.update({ key: "selectedContactID", value: ctx.payload }),
      db.uiSpvMain.update({
        key: "otherCurrentRecord",
        value: spvMain.currentRecord,
      }),
    ]);
    yield* next();
  }
);

export const showCtrcorLoader = xThunk.create<TContractorFormSource>(
  "/xthnk/showCtrcorLoader",
  { supervisor: takeLeading },
  function* (ctx: ThunkCtx, next: Next) {
    yield* schema.update(db.uiSpvMain.reset());
    ///
    yield* next();
  }
);

export const callFXMLDetailsForm = xThunk.create<TIvFiscal>(
  "/xthnk/callFXMLDetailsForm",
  { supervisor: takeLeading },
  function* (ctx: ThunkCtx, next: Next) {
    const ivoState = yield* select(db.uiInvoiceOverall.select);
    const { selectedCIF } = ivoState;
    if (!selectedCIF) {
      return;
    }
    yield* openFXMLDetailsForm(JSON.stringify({ ...ctx.payload, selectedCIF }));
    yield* next();
  }
);

function isEmpty(found: TExecution): boolean {
  //
  return !found || Object.keys(found).length === 0;
}
//todo: https://github.com/VldMrgnn/jk-react-app/issues/96 the "ERORI FACTURA" result type has to be inspected easyly , as the xml of the invoice is.
